import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import * as React from "react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import ToggleButton from "src/components/common/Buttons/ToggleButton";
import Tree, { ITree } from "src/components/common/Tree/Tree";
import { Link } from "src/components/routing";
import { FontAwesomeIcons } from "src/constants/fontAwesomeIcons";
import { LaboratoryPersonnelsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import useNavigate from "src/hooks/useNavigate";
import * as LaboratoryActions from "../../../actions/laboratories";
import * as LaboratoryPersonnelActions from "../../../actions/laboratoryPersonnels";
import * as NavigationActions from "../../../actions/navigation";
import { RestrictedLayout } from "../../../components/layouts/index";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { LaboratorySubMenu } from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";

import "./LabPersonnelsPage.scss";

interface LabPersonnelsPageProps {
    laboratory: Dtos.Laboratory;
    loadingLaboratory: boolean;
    loadLaboratorySuccess: boolean;
    loadLaboratoryFailure: boolean;
    loadLaboratoryErrorCode: string;
    laboratoryPersonnel: Dtos.LaboratoryPersonnel[];
    loadingLaboratoryPersonnel: boolean;
    loadLaboratoryPersonnelSuccess: boolean;
    loadLaboratoryPersonnelFailure: boolean;
    loadLaboratoryPersonnelErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearLaboratory: LaboratoryActions.ILaboratoryClearActionCreator;
    loadLaboratory: LaboratoryActions.ILaboratoryLoadByIdActionCreator;
    clearLaboratoryPersonnel: LaboratoryPersonnelActions.ILaboratoryPersonnelClearActionCreator;
    loadLaboratoryPersonnel: LaboratoryPersonnelActions.ILaboratoryPersonnelLoadByLaboratoryIdActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const LabPersonnelsPage: FC<LabPersonnelsPageProps> = ({
    laboratory,
    loadingLaboratory,
    loadLaboratorySuccess,
    loadLaboratoryFailure,
    loadLaboratoryErrorCode,
    laboratoryPersonnel,
    loadingLaboratoryPersonnel,
    loadLaboratoryPersonnelSuccess,
    loadLaboratoryPersonnelFailure,
    loadLaboratoryPersonnelErrorCode,
    permissions,
    clearLaboratory,
    loadLaboratory,
    clearLaboratoryPersonnel,
    loadLaboratoryPersonnel,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { laboratoryId } = useParams<{ laboratoryId: string }>();
    const laboratoryIdNumber = parseInt(laboratoryId, 10);

    //
    // -- States --
    const [showCeased, setShowCeased] = useState<boolean>(false);
    const [treeData, setTreeData] = useState<ITree | null>(null);
    const [initalizedTreeData, setInitalizedTreeData] =
        useState<boolean>(false);
    const [toggleState, setToggleState] = useState<string[]>([]);
    const [creatingTree, setCreatingTree] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- Variables --
    const showPersonnels =
        !loadingLaboratory && 
        loadLaboratorySuccess && 
        !loadingLaboratoryPersonnel && 
        loadLaboratoryPersonnelSuccess && 
        !creatingTree;


    // Load Laboratory & Personnel
    useEffect(() => {
        if (laboratoryIdNumber && !laboratory) {
            setDrawerItems(LaboratorySubMenu(laboratoryIdNumber, permissions));
            setBreadcrumbs(LaboratoryPersonnelsBreadcrumbs(laboratoryIdNumber));

            setTreeData(null);

            loadLaboratory(laboratoryIdNumber);
            loadLaboratoryPersonnel(laboratoryIdNumber);
        }
    }, [
        laboratoryIdNumber,
        laboratory,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        loadLaboratory,
        loadLaboratoryPersonnel,
        setTreeData,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (laboratoryIdNumber && laboratory?.name) {
            setBreadcrumbs(
                LaboratoryPersonnelsBreadcrumbs(
                    laboratoryIdNumber,
                    laboratory.name
                )
            );
        }
    }, [
        laboratory,
        laboratoryIdNumber,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadLaboratoryFailure) {
            navigate(`/error/${loadLaboratoryErrorCode}`);
        }
        if (loadLaboratoryPersonnelFailure) {
            navigate(`/error/${loadLaboratoryPersonnelErrorCode}`);
        }
    }, [
        loadLaboratoryFailure, 
        loadLaboratoryErrorCode, 
        loadLaboratoryPersonnelFailure, 
        loadLaboratoryPersonnelErrorCode, 
        navigate
    ]);

    // Cleanup effect to clear the laboratory & personnel on component unmount
    useEffect(() => {
        return () => {
            clearLaboratory();
            clearLaboratoryPersonnel();
            setTreeData(null);
        };
    }, [clearLaboratory, clearLaboratoryPersonnel, setTreeData]);

    const onChange = (newValue) => {
        setInitalizedTreeData(false);
        setShowCeased(newValue);
    };

    // Refresh tree
    useEffect(() => {
        if (laboratoryPersonnel) {
            setInitalizedTreeData(false);
        }
    }, [laboratoryPersonnel, setInitalizedTreeData]);

    // Create tree
    useEffect(() => {
        if (laboratoryPersonnel && !initalizedTreeData) {
            setCreatingTree(true);

            // If no laboratory personnel return
            if (laboratoryPersonnel?.length == 0) {
                setCreatingTree(false);
                return;
            }

            let uniqueRoles: Dtos.Role[] = [];
            if (showCeased) {
                uniqueRoles = Array.from(
                    new Map(
                        laboratoryPersonnel?.map((x) => [
                            x.role.id,
                            x.role,
                        ])
                    ).values()
                );
            } else {
                uniqueRoles = Array.from(
                    new Map(
                        laboratoryPersonnel
                            ?.filter((x) => !x.dateCeased)
                            .map((x) => [x.role.id, x.role])
                    ).values()
                );
            }

            const createChildNodes = (roleId, showCeased) => {
                let children = [];

                if (showCeased) {
                    children =
                        laboratoryPersonnel
                            ?.filter((y) => y.roleId == roleId)
                            ?.sort((a, b) => a?.id - b?.id)
                            ?.map((y) => ({
                                id: `personnel-${y.id}`,
                                data: (
                                    <div
                                        className={`form-row ${
                                            y?.dateCeased ? "ceased" : ""
                                        }`}
                                    >
                                        <div className="col-1">
                                            <FontAwesomeIcon
                                                icon={
                                                    FontAwesomeIcons.Duotone
                                                        .USER
                                                }
                                            />
                                        </div>
                                        <div className="col">
                                            {
                                                y?.personnel
                                                    ?.displayWithSalutation
                                            }
                                        </div>
                                        <div className="col">
                                            {y?.dateStarted
                                                ? moment(
                                                        y?.dateStarted,
                                                        "YYYY-MM-DD HH:mm:ss.SSSSSSS Z"
                                                    ).format("DD/MM/YYYY")
                                                : ""}
                                        </div>
                                        <div className="col">
                                            {y?.dateCeased
                                                ? moment(
                                                        y?.dateCeased,
                                                        "YYYY-MM-DD HH:mm:ss.SSSSSSS Z"
                                                    ).format("DD/MM/YYYY")
                                                : ""}
                                        </div>
                                    </div>
                                ),
                                icon: null,
                                collapsible: false,
                            })) ?? [];
                } else {
                    children =
                        laboratoryPersonnel
                            ?.filter((y) => y.roleId == roleId && !y.dateCeased)
                            ?.sort((a, b) => a?.id - b?.id)
                            ?.map((y) => ({
                                id: `personnel-${y?.id}`,
                                data: (
                                    <div className="form-row">
                                        <div className="col-1">
                                            <FontAwesomeIcon
                                                icon={
                                                    FontAwesomeIcons.Duotone
                                                        .USER
                                                }
                                            />
                                        </div>
                                        <div className="col">
                                            {
                                                y?.personnel
                                                    ?.displayWithSalutation
                                            }
                                        </div>
                                        <div className="col">
                                            {y?.dateStarted
                                                ? moment(
                                                        y?.dateStarted,
                                                        "YYYY-MM-DD HH:mm:ss.SSSSSSS Z"
                                                    ).format("DD/MM/YYYY")
                                                : ""}
                                        </div>
                                        <div className="col">
                                            {y?.dateCeased
                                                ? moment(
                                                        y?.dateCeased,
                                                        "YYYY-MM-DD HH:mm:ss.SSSSSSS Z"
                                                    ).format("DD/MM/YYYY")
                                                : ""}
                                        </div>
                                    </div>
                                ),
                                icon: null,
                                collapsible: false,
                            })) ?? [];
                }

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: `tableheader`,
                        data: (
                            <div className="form-row">
                                <div className="col-1"></div>
                                <div className="col">
                                    <strong>Name</strong>
                                </div>
                                <div className="col">
                                    <strong>Date Started</strong>
                                </div>
                                <div className="col">
                                    <strong>Date Ceased</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            let treeNodes =
                uniqueRoles
                    ?.sort((a, b) => a?.value?.localeCompare(b?.value))
                    ?.map((x) => ({
                        id: `role-${x.id}`,
                        data: (
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon
                                    icon={FontAwesomeIcons.Duotone.ROLE}
                                />
                                <span
                                    className="pl-4 mb-0"
                                    style={{ width: "275px" }}
                                >
                                    {x?.value}
                                </span>
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: "50px" }}
                                >
                                    <span className="badge badge-pill badge-secondary ml-4 mb-0">
                                        {
                                            laboratoryPersonnel?.filter(
                                                (y) =>
                                                    showCeased
                                                        ? y.roleId == x.id
                                                        : y.roleId == x.id &&
                                                            !y.dateCeased
                                            )?.length
                                        }
                                    </span>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: true,
                        subTree: {
                            children: [...createChildNodes(x.id, showCeased)],
                        },
                    })) ?? [];

            // Initialize tree toggle
            const expanded = treeNodes.map((z) => z.id);

            setToggleState(expanded);
            setTreeData({ children: treeNodes });
            setInitalizedTreeData(true);
            setCreatingTree(false);
        }
    }, [
        laboratoryPersonnel,
        initalizedTreeData,
        treeData,
        setTreeData,
        setToggleState,
        setInitalizedTreeData,
        showCeased,
        setCreatingTree,
    ]);

    const handleToggle = useCallback(
        (id: string, navigateFlag?: boolean) => {
            if (navigateFlag) {
                if (
                    id.includes("personnel") &&
                    AuthenticationHelper.hasPermission(
                        permissions,
                        Dtos.Permission.InstitutionPersonnelView
                    )
                ) {
                    navigate(
                        `/labs/${laboratoryIdNumber}/personnel/${id.replace(
                            "personnel-",
                            ""
                        )}/details`
                    );
                }
                if (id.includes("role")) {
                    setToggleState((prev) =>
                        prev.includes(id)
                            ? prev.filter((nodeId) => nodeId !== id)
                            : [...prev, id]
                    );
                }
            } else {
                setToggleState((prev) =>
                    prev.includes(id)
                        ? prev.filter((nodeId) => nodeId !== id)
                        : [...prev, id]
                );
            }
        },
        [setToggleState, navigate, laboratoryIdNumber, permissions, laboratory]
    );

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className="col-6 mb-2">
                        <h1>Personnel</h1>
                    </div>
                    <div
                        className="col-md-12 col-lg d-flex justify-content-md-start justify-content-lg-end align-items-stretch mb-2 mb-lg-0"
                        style={{ height: "38px" }}
                    >
                        <ToggleButton
                            id="toggle-button-ceased"
                            checked={showCeased}
                            onChange={onChange}
                            labelA="Hide ceased personnel"
                            labelB="Show ceased personnel"
                            style={{ minWidth: "200px" }}
                        />
                        <a
                            className="btn btn-info mr-2"
                            target="_blank"
                            href={encodeURI(
                                `/api/personnelrole/export/0/0/0/${laboratoryIdNumber}`
                            )}
                        >
                            <FontAwesomeIcon icon={FontAwesomeIcons.Solid.CLOUD_DOWNLOAD} fixedWidth />
                        </a>
                        {laboratory &&
                            AuthenticationHelper.hasPermission(
                                permissions,
                                Dtos.Permission.LabPersonnelCreate,
                                null,
                                null
                            ) && (
                                <Link
                                    className={"btn btn-primary"}
                                    url={`/labs/${laboratoryIdNumber}/personnel/create`}
                                >
                                    Create
                                </Link>
                            )}
                    </div>
                </div>
                {!showPersonnels ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showPersonnels ? (
                    <>
                        {treeData && treeData?.children?.length > 0 ? (
                            <Tree
                                treeData={treeData}
                                onToggle={handleToggle}
                                toggleState={toggleState}
                            />
                        ) : (
                            <div className="card card-shadow p-3">
                                <div className="alert alert-info d-flex mb-0">
                                    <div className="mr-2">
                                        <FontAwesomeIcon
                                            icon={
                                                FontAwesomeIcons.Solid
                                                    .INFO_SQUARE
                                            }
                                            fixedWidth
                                        />
                                    </div>
                                    <span>
                                        There are currently no laboratory
                                        personnel
                                    </span>
                                </div>
                            </div>
                        )}
                    </>
                ) : null}
            </div>
        );
    }, [
        AuthenticationHelper,
        permissions,
        showPersonnels,
        laboratory,
        onChange,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.LabPersonnelView}
            loading={loadingLaboratory || loadingLaboratoryPersonnel}
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<LabPersonnelsPageProps> => {
    let laboratory: Dtos.Laboratory | undefined = !(
        state.laboratories.formData instanceof Array
    )
        ? state.laboratories.formData
        : undefined;
    let laboratoryPersonnel: Dtos.LaboratoryPersonnel[] | undefined = (
        state.laboratoryPersonnels.formData instanceof Array
    )
        ? state.laboratoryPersonnels.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        laboratory: laboratory!,
        loadingLaboratory:
            state.laboratories.loadState &&
            state.laboratories.loadState.status == RequestState.Pending,
        loadLaboratorySuccess:
            state.laboratories.loadState &&
            state.laboratories.loadState.status == RequestState.Success,
        loadLaboratoryFailure:
            state.laboratories.loadState &&
            state.laboratories.loadState.status == RequestState.Failure,
        loadLaboratoryErrorCode: state.laboratories.loadState.errorCode,
        laboratoryPersonnel: laboratoryPersonnel!,
        loadingLaboratoryPersonnel:
            state.laboratoryPersonnels.loadState &&
            state.laboratoryPersonnels.loadState.status == RequestState.Pending,
        loadLaboratoryPersonnelSuccess:
            state.laboratoryPersonnels.loadState &&
            state.laboratoryPersonnels.loadState.status == RequestState.Success,
        loadLaboratoryPersonnelFailure:
            state.laboratoryPersonnels.loadState &&
            state.laboratoryPersonnels.loadState.status == RequestState.Failure,
        loadLaboratoryPersonnelErrorCode: state.laboratoryPersonnels.loadState.errorCode,
        permissions: permissions,
    };
};

const mapDispatchToProps = (dispatch): Partial<LabPersonnelsPageProps> => {
    return {
        loadLaboratory: bindActionCreators(
            LaboratoryActions.LoadLaboratoryById,
            dispatch
        ),
        clearLaboratory: bindActionCreators(LaboratoryActions.Clear, dispatch),
        loadLaboratoryPersonnel: bindActionCreators(LaboratoryPersonnelActions.LoadLaboratoryPersonnelByLaboratoryId, dispatch),
        clearLaboratoryPersonnel: bindActionCreators(LaboratoryPersonnelActions.Clear, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(LabPersonnelsPage);
